// Faq1.js - Contains functions for creating a FAQ section with collapsible items
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Frequently Asked Questions section with collapsible items
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the FAQ section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The FAQ section elements
 */
export const createFaqElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const faqSectionClass = 'faq-section';
  const faqContainerClass = 'faq-container';
  const faqHeaderClass = 'faq-header';
  const faqTitleClass = 'faq-title';
  const faqSubtitleClass = 'faq-subtitle';
  const faqItemsClass = 'faq-items';
  const faqItemClass = 'faq-item';
  const faqItemActiveClass = 'faq-item-active';
  const faqQuestionClass = 'faq-question';
  const faqAnswerClass = 'faq-answer';
  const faqAnswerActiveClass = 'faq-answer-active';
  const faqToggleClass = 'faq-toggle';
  const faqToggleActiveClass = 'faq-toggle-active';
  
  // Define styles
  // Desktop Styles (default)
  const faqSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f9fafb',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const faqContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '900px',
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  const faqHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '50px',
    maxWidth: '800px'
  };

  const faqTitleStyles = {
    fontSize: '42px',
    fontWeight: '800',
    marginBottom: '20px',
    color: '#111827',
    lineHeight: '1.2'
  };

  const faqSubtitleStyles = {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#4b5563',
    maxWidth: '700px'
  };

  const faqItemsStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '16px'
  };

  const faqItemStyles = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    transition: 'all 0.3s ease'
  };

  const faqItemActiveStyles = {
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    borderColor: '#d1d5db'
  };

  const faqQuestionStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '18px',
    paddingBottom: '18px',
    paddingLeft: '24px',
    paddingRight: '24px',
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    cursor: 'pointer',
    borderBottom: '1px solid transparent',
    transition: 'all 0.3s ease'
  };

  const faqAnswerStyles = {
    paddingTop: '0',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
    height: '0',
    maxHeight: '0',
    opacity: '0',
    overflow: 'hidden',
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#4b5563',
    transition: 'all 0.3s ease'
  };

  const faqAnswerActiveStyles = {
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '24px',
    paddingRight: '24px',
    height: 'auto',
    maxHeight: '1000px',
    opacity: '1',
    borderTop: '1px solid #e5e7eb'
  };

  const faqToggleStyles = {
    width: '20px',
    height: '20px',
    position: 'relative',
    display: 'inline-block',
    marginLeft: '10px'
  };

  const faqToggleActiveStyles = {
    '&::before': {
      transform: 'rotate(45deg)'
    },
    '&::after': {
      transform: 'rotate(135deg)'
    }
  };

  // Tablet Styles
  const tabletFaqTitleStyles = {
    fontSize: '36px'
  };

  // Mobile Styles
  const mobileFaqTitleStyles = {
    fontSize: '30px'
  };

  const mobileFaqQuestionStyles = {
    fontSize: '16px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '20px',
    paddingRight: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(faqSectionClass, faqSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(faqContainerClass, faqContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(faqHeaderClass, faqHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(faqTitleClass, faqTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(faqSubtitleClass, faqSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(faqItemsClass, faqItemsStyles);
    newRegistry[DEVICES.DESKTOP].set(faqItemClass, faqItemStyles);
    newRegistry[DEVICES.DESKTOP].set(faqItemActiveClass, faqItemActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(faqQuestionClass, faqQuestionStyles);
    newRegistry[DEVICES.DESKTOP].set(faqAnswerClass, faqAnswerStyles);
    newRegistry[DEVICES.DESKTOP].set(faqAnswerActiveClass, faqAnswerActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(faqToggleClass, faqToggleStyles);
    newRegistry[DEVICES.DESKTOP].set(faqToggleActiveClass, faqToggleActiveStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(faqTitleClass, tabletFaqTitleStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(faqTitleClass, mobileFaqTitleStyles);
    newRegistry[DEVICES.MOBILE].set(faqQuestionClass, mobileFaqQuestionStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    faqSectionClass,
    faqContainerClass,
    faqHeaderClass,
    faqTitleClass,
    faqSubtitleClass,
    faqItemsClass,
    faqItemClass,
    faqItemActiveClass,
    faqQuestionClass,
    faqAnswerClass,
    faqAnswerActiveClass,
    faqToggleClass,
    faqToggleActiveClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  const itemsId = Date.now() + 5;
  
  // FAQ items
  const item1Id = Date.now() + 6;
  const question1Id = Date.now() + 7;
  const answer1Id = Date.now() + 8;
  
  const item2Id = Date.now() + 10;
  const question2Id = Date.now() + 11;
  const answer2Id = Date.now() + 12;
  
  const item3Id = Date.now() + 14;
  const question3Id = Date.now() + 15;
  const answer3Id = Date.now() + 16;
  
  const item4Id = Date.now() + 18;
  const question4Id = Date.now() + 19;
  const answer4Id = Date.now() + 20;

  const item5Id = Date.now() + 22;
  const question5Id = Date.now() + 23;
  const answer5Id = Date.now() + 24;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'FAQ Section',
      parentId: null,
      className: faqSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'faq-section',
        'data-component': 'section',
        'data-section-type': 'faq',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: faqContainerClass,
      styles: {},
      children: [headerId, itemsId],
      text: '',
      attributes: {
        'data-element': 'faq-container'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: faqHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'faq-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'FAQ Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: faqTitleClass,
      styles: {},
      children: [],
      text: 'Frequently Asked Questions',
      attributes: {
        'data-element': 'faq-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: faqSubtitleClass,
      styles: {},
      children: [],
      text: 'Find answers to the most common questions about our services and how we can help you achieve your goals.',
      attributes: {
        'data-element': 'faq-subtitle'
      }
    },
    
    // FAQ Items container
    {
      id: itemsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Items',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: faqItemsClass,
      styles: {},
      children: [item1Id, item2Id, item3Id, item4Id, item5Id],
      text: '',
      attributes: {
        'data-element': 'faq-items'
      }
    },
    
    // FAQ Item 1
    {
      id: item1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Item 1',
      parentId: itemsId,
      hierarchyName: getHierarchyName('div'),
      className: `${faqItemClass} ${faqItemActiveClass}`,
      styles: {},
      children: [question1Id, answer1Id],
      text: '',
      attributes: {
        'data-element': 'faq-item',
        'data-faq-index': '1',
        'data-active': 'true'
      }
    },
    
    // FAQ Question 1
    {
      id: question1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Question 1',
      parentId: item1Id,
      hierarchyName: getHierarchyName('div'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'What services do you offer?',
      attributes: {
        'data-element': 'faq-question'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1000,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer1Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer1Id}`
          }
        ]
      }
    },
    
    // FAQ Answer 1
    {
      id: answer1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Answer 1',
      parentId: item1Id,
      hierarchyName: getHierarchyName('div'),
      className: `${faqAnswerClass} ${faqAnswerActiveClass}`,
      styles: {},
      children: [],
      text: 'We offer a comprehensive range of digital services including web design and development, mobile app development, e-commerce solutions, digital marketing, and brand strategy. Our team of experts works closely with you to understand your needs and deliver tailored solutions that help your business grow.',
      attributes: {
        'data-element': 'faq-answer',
        'data-active': 'true'
      }
    },
    
    // FAQ Item 2
    {
      id: item2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Item 2',
      parentId: itemsId,
      hierarchyName: getHierarchyName('div'),
      className: faqItemClass,
      styles: {},
      children: [question2Id, answer2Id],
      text: '',
      attributes: {
        'data-element': 'faq-item',
        'data-faq-index': '2'
      }
    },
    
    // FAQ Question 2
    {
      id: question2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Question 2',
      parentId: item2Id,
      hierarchyName: getHierarchyName('div'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'How much does your service cost?',
      attributes: {
        'data-element': 'faq-question'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1001,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer2Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer2Id}`
          }
        ]
      }
    },
    
    // FAQ Answer 2
    {
      id: answer2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Answer 2',
      parentId: item2Id,
      hierarchyName: getHierarchyName('div'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Our pricing is tailored to your specific needs and the scope of your project. We offer flexible pricing models including fixed project rates, hourly rates, and retainer packages. Contact us for a free consultation and quote based on your requirements. We strive to provide competitive rates while delivering exceptional value.',
      attributes: {
        'data-element': 'faq-answer'
      }
    },
    
    // FAQ Item 3
    {
      id: item3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Item 3',
      parentId: itemsId,
      hierarchyName: getHierarchyName('div'),
      className: faqItemClass,
      styles: {},
      children: [question3Id, answer3Id],
      text: '',
      attributes: {
        'data-element': 'faq-item',
        'data-faq-index': '3'
      }
    },
    
    // FAQ Question 3
    {
      id: question3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Question 3',
      parentId: item3Id,
      hierarchyName: getHierarchyName('div'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'How long does it take to complete a project?',
      attributes: {
        'data-element': 'faq-question'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1002,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer3Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer3Id}`
          }
        ]
      }
    },
    
    // FAQ Answer 3
    {
      id: answer3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Answer 3',
      parentId: item3Id,
      hierarchyName: getHierarchyName('div'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Project timelines vary depending on complexity, scope, and your specific requirements. A simple website might take 2-4 weeks, while a complex e-commerce platform or custom application could take 3-6 months. During our initial consultation, we\'ll provide a detailed timeline with key milestones. We\'re committed to delivering quality work within the agreed timeframe.',
      attributes: {
        'data-element': 'faq-answer'
      }
    },
    
    // FAQ Item 4
    {
      id: item4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Item 4',
      parentId: itemsId,
      hierarchyName: getHierarchyName('div'),
      className: faqItemClass,
      styles: {},
      children: [question4Id, answer4Id],
      text: '',
      attributes: {
        'data-element': 'faq-item',
        'data-faq-index': '4'
      }
    },
    
    // FAQ Question 4
    {
      id: question4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Question 4',
      parentId: item4Id,
      hierarchyName: getHierarchyName('div'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'Do you offer ongoing support and maintenance?',
      attributes: {
        'data-element': 'faq-question'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1003,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer4Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer4Id}`
          }
        ]
      }
    },
    
    // FAQ Answer 4
    {
      id: answer4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Answer 4',
      parentId: item4Id,
      hierarchyName: getHierarchyName('div'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Yes, we offer comprehensive support and maintenance packages to ensure your digital solutions continue to perform optimally. Our support plans include regular updates, security patches, performance monitoring, and technical assistance. We also provide training for your team to help you maximize the value of your investment.',
      attributes: {
        'data-element': 'faq-answer'
      }
    },
    
    // FAQ Item 5
    {
      id: item5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Item 5',
      parentId: itemsId,
      hierarchyName: getHierarchyName('div'),
      className: faqItemClass,
      styles: {},
      children: [question5Id, answer5Id],
      text: '',
      attributes: {
        'data-element': 'faq-item',
        'data-faq-index': '5'
      }
    },
    
    // FAQ Question 5
    {
      id: question5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Question 5',
      parentId: item5Id,
      hierarchyName: getHierarchyName('div'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'How do I get started with your services?',
      attributes: {
        'data-element': 'faq-question'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1004,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer5Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer5Id}`
          }
        ]
      }
    },
    
    // FAQ Answer 5
    {
      id: answer5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Answer 5',
      parentId: item5Id,
      hierarchyName: getHierarchyName('div'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Getting started is easy! Simply contact us through our website form, email, or phone. We\'ll schedule a free consultation to discuss your needs and goals. From there, we\'ll develop a tailored proposal outlining our approach, timeline, and cost. Once approved, we\'ll kick off the project with a detailed planning phase to ensure we\'re aligned on expectations.',
      attributes: {
        'data-element': 'faq-answer'
      }
    }
  ];

  return {
    faqSection: newElements[0],
    elements: newElements
  };
};

export default createFaqElements; 